import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NavigationMenu } from './NavigationMenu';
import { UserMenu } from './UserMenu';
import { useSidebar } from './SidebarProvider';
import { useScreenSize } from '@/hooks/useScreenSize';
import { WelcomeModal } from '@/components/welcome/WelcomeModal';

/**
 * Main Sidebar Component
 * 
 * Complete sidebar implementation following Dieter Rams principles:
 * - Innovative: Modern responsive sidebar with smooth transitions
 * - Useful: Efficient navigation with collapse/expand functionality
 * - Aesthetic: Clean, minimal design with proper visual hierarchy
 * - Understandable: Intuitive navigation patterns
 * - Unobtrusive: Enhances navigation without dominating
 * - Honest: Clear states and functionality
 * - Long-lasting: Timeless design patterns
 * - Thorough: Complete responsive behavior
 * - Environmentally friendly: Supports dark/light modes
 * - Little design as possible: Minimal, essential elements
 */

interface SidebarBrandingProps {
  logo?: string;
  title: string;
  href: string;
  collapsed: boolean;
}

// Sidebar branding component.
//
// Alignment rules per state:
// - Expanded: logo's left edge sits at the same x as the nav-label text
//   glyph. Nav labels use `ml-3 mr-5 px-2`, so the text glyph starts at
//   `ml-3 + px-2 = 20px` from the sidebar's left.
// - Collapsed: logo is centered on the icon column. Icons use
//   `w-10 mx-auto`, so the icon center is at the sidebar's horizontal
//   center.
const SidebarBranding: React.FC<SidebarBrandingProps> = ({
  logo,
  title,
  href,
  collapsed,
}) => {
  return (
    <Link
      to={href}
      aria-label={title}
      className={cn(
        'block w-fit hover:opacity-80 group',
        collapsed ? 'mx-auto' : 'ml-5',
      )}
    >
      {logo ? (
        <img src={logo} alt={title} className="h-8 w-8" />
      ) : (
        <span className="font-medium text-lg text-foreground">{title}</span>
      )}
      {logo && <span className="sr-only">{title}</span>}
    </Link>
  );
};

// Sidebar component (always visible, collapses to icons on smaller screens)
const DesktopSidebar: React.FC<{ className?: string }> = ({ className }) => {
  const { config } = useSidebar();

  // Use consolidated screen size detection
  const { isDesktop } = useScreenSize();
  const location = useLocation();

  // Force collapse on the new-site wizard route.
  const forceCollapsed = location.pathname === '/sites/new';

  // Expanded on desktop, collapsed to icons on tablet/mobile or wizard.
  const isCollapsed = forceCollapsed || !isDesktop;
  const sidebarWidth = isCollapsed
    ? config.layout.collapsedWidth
    : config.layout.expandedWidth;

  return (
    <aside
      className={cn(
        "flex flex-col fixed z-50 pb-6",
        "top-0 bottom-0 left-0",
        "transition-[width] duration-200 ease-out",
        className
      )}
      style={{ width: sidebarWidth }}
      aria-label="Main navigation"
    >
      {/* Header with branding */}
      <div className="py-5">
        <SidebarBranding
          logo={config.navigation.branding.logo}
          title={config.navigation.branding.title}
          href={config.navigation.branding.href}
          collapsed={isCollapsed}
        />
      </div>

      {/* Scrollable nav */}
      <ScrollArea className="flex-1 overflow-visible">
        <NavigationMenu sections={config.navigation.sections} />
      </ScrollArea>

      {/* User menu pinned to bottom of aside */}
      <UserMenu config={config.navigation.userMenu} />
    </aside>
  );
};

// Main content wrapper that adjusts for sidebar
interface MainContentProps {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const MainContent: React.FC<MainContentProps> = ({
  children,
  className,
  'data-testid': dataTestId
}) => {
  const { config } = useSidebar();
  const { isDesktop } = useScreenSize();
  const location = useLocation();

  const forceCollapsed = location.pathname === '/sites/new';
  const expanded = isDesktop && !forceCollapsed;

  const sidebarWidth = expanded
    ? config.layout.expandedWidth
    : config.layout.collapsedWidth;

  return (
    <main
      id="main-content"
      className={cn(
        "min-h-screen",
        "ml-[var(--content-offset)]",
        "px-4 sm:px-6 lg:px-8",
        "transition-[margin-left] duration-200 ease-out",
        className
      )}
      style={{
        '--content-offset': sidebarWidth
      } as React.CSSProperties}
      data-testid={dataTestId}
    >
      {children}
    </main>
  );
};

// Layout wrapper component that includes the sidebar provider
interface SidebarLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  children,
  className
}) => {
  return (
    <div className={cn(
      "min-h-screen bg-background overflow-x-hidden",
      className
    )}>
      <WelcomeModal />

      {/* Skip link for accessibility — visually hidden until focused */}
      <a
        href="#main-content"
        className={cn(
          "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4",
          "z-50 px-4 py-2 bg-foreground text-background rounded-full",
          "focus:outline-none focus:ring-2 focus:ring-offset-2"
        )}
      >
        Skip to main content
      </a>

      {/* Sidebar - always visible, collapses to icons on smaller screens */}
      <DesktopSidebar />

      {/* Main content with responsive margin */}
      <MainContent>
        {children}
      </MainContent>
    </div>
  );
};

export default SidebarLayout;